import { CreateMetadataFieldRequest, MetadataFieldType } from '@/api/generatedApi';
import { useCreateMetadataFields } from '@/hooks/api/metadata/useCreateMetadataFields';
import { useToast } from '@/hooks/useToast';
import { createUseStyles } from '@/services/createUseStyles';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { MetadataTestIds } from '@/enums/metadataTestIds.enum';

const useStyles = createUseStyles({
  CreateMetadataFieldModal_form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '12px',
  },
});

export const CreateMetadataFieldModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const c = useStyles();
  const { showToast } = useToast();
  const { createMetadataField, isCreating, createError } = useCreateMetadataFields();
  const [formData, setFormData] = useState<CreateMetadataFieldRequest>({
    name: '',
    type: MetadataFieldType.Text,
  });

  const handleClose = () => {
    setFormData({ name: '', type: MetadataFieldType.Text });
    onClose();
  };

  const handleSubmit = () => {
    createMetadataField(formData, {
      onSuccess: () => {
        showToast({ message: 'Metadata field created successfully', type: 'success' });
        handleClose();
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <DialogTitle>Create Metadata Field</DialogTitle>
      <DialogContent>
        <Box className={c.CreateMetadataFieldModal_form}>
          {createError && <Alert severity='error'>{createError.message}</Alert>}
          <TextField
            label='Field Name'
            value={formData.name}
            onChange={event => setFormData({ ...formData, name: event.target.value })}
            fullWidth
            required
            slotProps={{
              input: {
                inputProps: { 'data-testid': MetadataTestIds.MetadataFieldNameInput },
              },
            }}
          />
          <FormControl fullWidth required>
            <InputLabel id='metadata-type-label'>Field Type</InputLabel>
            <Select
              labelId='metadata-type-label'
              value={formData.type}
              label='Field Type'
              onChange={event => setFormData({ ...formData, type: event.target.value as MetadataFieldType })}
              inputProps={{ 'data-testid': MetadataTestIds.MetadataFieldTypeSelect }}
            >
              <MenuItem value='text' data-testid={MetadataTestIds.MetadataFieldTypeText}>
                Text
              </MenuItem>
              <MenuItem value='number' data-testid={MetadataTestIds.MetadataFieldTypeNumber}>
                Number
              </MenuItem>
              <MenuItem value='boolean' data-testid={MetadataTestIds.MetadataFieldTypeBoolean}>
                Boolean
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          color='primary'
          disabled={isCreating || !formData.name.trim()}
          data-testid={MetadataTestIds.CreateMetadataFieldSubmitButton}
        >
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
